import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Candy, Factory, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Sugar = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/sugar"]?.title} description={seoData["industries/sugar"]?.description} path="/industries/sugar" keywords={seoData["industries/sugar"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Sugar Processing"
        description="Coating and repair for sugar processing facilities. Erosion and corrosion protection for evaporators, crystallizers, tanks, and material handling equipment in sugar mills and refineries."
        backgroundKey="sugar"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Severe erosion on cane handling and conveying equipment',
              'Evaporator and crystallizer corrosion from hot syrup and brine',
              'Juice tank and storage vessel deterioration',
              'Floor wear from heavy equipment and sugar crystal abrasion',
              'Boiler and steam system corrosion',
              'Cooling water system scaling and corrosion'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-amber-50 rounded-lg">
                <Candy className="text-amber-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Factory className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Evaporator & Process Vessel Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal coating and repair for evaporators, crystallizers, vacuum pans, and juice tanks. Chemical and heat-resistant coating systems for continuous sugar processing service.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Factory className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Material Handling Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Abrasion-resistant coatings for cane conveyors, sugar bin linings, chutes, and hoppers. Ceramic-filled systems for extreme erosion environments.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Factory className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Floor & Containment Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Heavy-duty floor coating systems for processing areas, warehouses, and packaging areas. Secondary containment for chemical storage and boiler fuel areas.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Factory className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Utility System Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Boiler repair, steam pipe coating, and cooling water system restoration. Condenser and heat exchanger repair for mill utility infrastructure.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Sugar Processing?</h2>
              <div className="space-y-3">
                {['Combined erosion and corrosion resistance for sugar processing',
                  'High-temperature coating systems for evaporation and crystallization',
                  'Campaign and off-season scheduling for minimal production impact',
                  'On-site blasting and surface preparation capabilities',
                  'Fast-cure systems for time-critical repairs',
                  'Experience with seasonal processing facility demands'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Sugar Mill Experience</h3>
              <p className="text-amber-100 mb-6">BCS understands the seasonal demands and unique corrosion challenges of sugar processing. Our coating solutions are designed for the combined heat, chemical, and abrasion exposure in sugar mills.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/equipment-repair')}>
                View Equipment Repair <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Sugar Mill Equipment</h2>
          <p className="text-blue-100 mb-8">Contact us to plan maintenance and coating work during your off-season.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Sugar;
